import { Button } from '../../../components/ui/Button';
import { SegmentedControl, Textarea } from '../../../components/ui/Field';
import { Section } from '../../../components/ui/Panel';

/**
 * The message body and the campaign's actions.
 *
 * Message Type lives here as the editor mode: HTML and Plain share one body field, and
 * the type decides how that body is sent and previewed.
 *
 * Send and Stop sit in the footer of this card. On wide screens the card is sticky and
 * viewport-tall, so they stay in reach while the operator scrolls the settings columns.
 */
export function EmailContentCard({
  form,
  setField,
  fieldError,
  className,
  onPreview,
  onHowToUse,
  onGenerateLink,
  onManageLinks,
  onSend,
  onStop,
  isSubmitting,
  isStopping,
  canStop,
  isTestMode,
  laneBanner
}) {
  const isHtml = form.messageType === 'HTML';
  const messageError = fieldError('message');
  const length = form.message?.length ?? 0;

  return (
    <Section
      title="Email Content"
      icon="fa-code"
      className={className}
      bodyClassName="gap-2"
      actions={
        <Button
          variant="ghost"
          size="icon"
          icon="fa-circle-question"
          onClick={onHowToUse}
          title="How to use"
          aria-label="How to use"
        />
      }
      footer={
        <div className="flex flex-col gap-2 border-t border-line bg-ink-25 p-3">
          {laneBanner}
          <div className="flex flex-wrap items-stretch gap-2">
            <Button
              variant="primary"
              size="lg"
              icon="fa-paper-plane"
              onClick={onSend}
              loading={isSubmitting}
              loadingLabel="Submitting…"
              className="min-w-[9rem] flex-1 whitespace-nowrap"
            >
              Send Email
              <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] tracking-normal normal-case">
                {isTestMode ? 'Test' : 'Bulk'}
              </span>
            </Button>

            {/* One of two entry points to the same stop operation; the other is in the
                interval panel. Both post /stop-sending. Shown only while there is a
                campaign to stop, because that is the whole window in which stopping
                means anything. */}
            {canStop ? (
              <Button
                variant="danger"
                size="lg"
                icon="fa-circle-stop"
                onClick={onStop}
                loading={isStopping}
                loadingLabel="Stopping…"
                className="min-w-[9rem] flex-1 whitespace-nowrap"
              >
                Stop Sending
              </Button>
            ) : null}
          </div>
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="success" size="sm" icon="fa-eye" onClick={onPreview}>
          Preview
        </Button>
        {onGenerateLink ? (
          <Button variant="primary" size="sm" icon="fa-link" onClick={onGenerateLink}>
            Generate Link
          </Button>
        ) : null}
        {onManageLinks ? (
          <Button variant="secondary" size="sm" icon="fa-list" onClick={onManageLinks}>
            Manage Links
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <label htmlFor="message" className="text-xs font-semibold text-ink-700">
            {isHtml ? 'Message / HTML' : 'Message / Plain text'}
            <span className="ml-0.5 text-danger-500" aria-hidden="true">*</span>
          </label>
          <span className="text-xs tabular-nums text-muted">{length.toLocaleString()} chars</span>
        </span>
        <SegmentedControl
          name="message-type"
          label="Message Type"
          value={form.messageType}
          onChange={(value) => setField('messageType', value)}
          options={[
            { value: 'HTML', label: 'HTML', icon: 'fa-code' },
            { value: 'Plain', label: 'Plain', icon: 'fa-align-left' }
          ]}
        />
      </div>

      <Textarea
        id="message"
        placeholder={isHtml ? '<!DOCTYPE html>…' : 'Enter the message body'}
        value={form.message}
        onChange={(event) => setField('message', event.target.value)}
        rows={14}
        spellCheck={!isHtml}
        className={
          isHtml
            ? 'min-h-[320px] flex-1 resize-none font-mono text-xs leading-snug'
            : 'min-h-[320px] flex-1 resize-none text-sm'
        }
        aria-invalid={messageError ? true : undefined}
      />
      {messageError || fieldError('messageType') ? (
        <span className="text-xs text-danger-600" role="alert">
          {messageError ?? fieldError('messageType')}
        </span>
      ) : null}
    </Section>
  );
}
